/**
 * Dam scope — consolidação e recorte de barragens (A/B/C) — GUPM-156.
 *
 * TypeScript PURO (regra de domínio #1): sem React, Supabase ou DOM.
 * O hook `useDamScope` compõe estas funções com os datasources; as telas
 * (Header/Dashboard) consomem o resultado sem saber de onde vêm os dados nem
 * da política de acesso.
 *
 * Consome o modelo "Observação" do GUPM-157 (`computeStatusEstrutura`,
 * `piorStatus`) — ver [[project_geoguard_upm]] e `status.ts`.
 */
import { piorStatus, type StatusEstrutura, type StatusEstruturaResult } from './status';
import type { TarpLevel } from '../types';
import { foraDeServico } from '../instrumentos/fora-de-servico';

/** Barragem visível no escopo: identidade mínima + status consolidado calculado. */
export interface BarragemStatus {
  id: string;
  code: string;
  name: string;
  status: StatusEstruturaResult;
}

/**
 * Recorte de acesso por barragem — **SLOT de ACL** (choke point).
 *
 * Hoje: chamado SEM `codigosPermitidos` → passthrough (todos veem todas as
 * ativas). Futuro: passar os códigos permitidos ao perfil recorta aqui, num
 * único ponto — trocar "todas as ativas" por "ativas permitidas ao perfil" é
 * mudar só esta chamada, sem tocar nas telas.
 */
export function filtrarBarragensPorAcl<T extends { code: string }>(
  barragens: readonly T[],
  codigosPermitidos?: readonly string[],
): T[] {
  if (!codigosPermitidos) return [...barragens];
  const permitidos = new Set(codigosPermitidos);
  return barragens.filter((b) => permitidos.has(b.code));
}

/**
 * Conta, por barragem (`damId`), **instrumentos** "em nível" — algum canal com
 * leitura atual `level ≠ NORMAL`. Ignora `NORMAL` e `null` (fora de vigência da Carta).
 * Único sinal automático real hoje (Opção A do GUPM-157); os demais encaixes
 * (perda de sinal, TARP, declaração) entram depois sem mudar esta função.
 *
 * GUPM-270 — conta INSTRUMENTO, não linha. Desde que a `readings_current` passou a
 * devolver uma linha por (instrumento, canal), um tiltímetro com os DOIS eixos fora
 * da faixa entrava duas vezes: a tela diria "2 instrumentos em nível" onde há um só.
 * Um `Set` de `instrumentId` resolve — e a contagem segue sensível a qualquer canal,
 * que é o que importa para o sinal automático (basta um eixo estourar).
 *
 * **GUPM-307 — `instrumentos` é OBRIGATÓRIO**, e é o que exclui os declarados fora de
 * serviço. Antes esta função só recebia leituras, e por isso era o único ponto de render
 * do app que a declaração não alcançava: a leitura congelada de um instrumento retirado de
 * campo contava como "instrumento em nível", virava `OBSERVACAO` em
 * `computeStatusEstrutura` e pintava o chip da barragem no header — que aparece em TODA
 * tela. Medido em 26/08/2026: a Barragem B tinha 1 instrumento em nível (o `INA-12A`) e
 * ZERO TARPs abertas, então o chip dela estava aceso exclusivamente por causa dele.
 *
 * Obrigatório e sem default de propósito: um parâmetro opcional aqui deixaria o próximo
 * chamador reintroduzir o defeito sem nada acusar, e este ponto já escapou de um rollout
 * inteiro dirigido pelo compilador — o `tsc` não tinha como apontar uma função cuja
 * assinatura nem mencionava o instrumento.
 */
export function contarEmNivelPorBarragem(
  leituras: readonly { damId: string; instrumentId: string; level: TarpLevel | null }[],
  instrumentos: readonly { id: string; foraDeServicoDesde: Date | null }[],
): Record<string, number> {
  const declarados = new Set(
    instrumentos.filter((i) => foraDeServico(i)).map((i) => i.id),
  );
  const porBarragem = new Map<string, Set<string>>();
  for (const r of leituras) {
    if (r.level && r.level !== 'NORMAL' && !declarados.has(r.instrumentId)) {
      const set = porBarragem.get(r.damId) ?? new Set<string>();
      set.add(r.instrumentId);
      porBarragem.set(r.damId, set);
    }
  }
  return Object.fromEntries([...porBarragem].map(([damId, set]) => [damId, set.size]));
}

/**
 * Consolida o status das barragens visíveis: devolve o breakdown por barragem
 * (preservado) e o `consolidado` = pior status entre elas. Sem barragens →
 * `NORMAL` (base segura).
 */
export function consolidarBarragens(barragens: readonly BarragemStatus[]): {
  porBarragem: BarragemStatus[];
  consolidado: StatusEstrutura;
} {
  const consolidado = barragens.reduce<StatusEstrutura>(
    (acc, b) => piorStatus(acc, b.status.nivel),
    'NORMAL',
  );
  return { porBarragem: [...barragens], consolidado };
}
